/**
 * Lexicon compilation: authored vocabulary → frozen, indexed lookup structure
 * (plan §3.2). Compile once per vocabulary; parse against it many times.
 *
 * @remarks
 * Result-typed per D07 — authored-content errors are data, reported ALL
 * together with locations (the same messages a future creator studio shows an
 * author), never first-failure. Authored tokens are trimmed and lowercased on
 * intake, so definitions written as `Take` still compile; duplicates that
 * emerge from case-folding are reported like any other collision.
 *
 * The compiled lexicon structurally satisfies the splitter's
 * `ConjunctionSplitVocabulary` port: a token "leads a clause" when it is an
 * intent token, an intent alias, the first word of a multiword alias, or a
 * direction token.
 *
 * @packageDocumentation
 */

import { type DirectionToken, type IntentToken, toDirectionToken, toIntentToken } from '../types.js';
import { COLON_TOKEN_ISSUE, isValidSingleWord, normalizeAuthoredToken } from './authored-token.js';
import { BUILTIN_MOVEMENT_INTENT, BUILTIN_STOPWORDS, RESERVED_FUNCTION_WORDS } from './builtin-function-words.js';
import { compileBareDirection } from './compile-bare-direction.js';
import { collectPrepositionBindings } from './compile-preposition-bindings.js';
import {
	type IntentDefinition,
	type LexiconDefinition,
	type ObjectPolicy,
	type ObjectPreference,
	type PrepositionRole,
} from './lexicon-definition.js';

/** One intent's compiled record. */
export interface CompiledIntent {
	/** D227: the direction this verb stands for alone (`climb` → `up`) — see the definition's remarks. Absent for most verbs. */
	readonly bareDirection?: DirectionToken;
	readonly canonicalToken: IntentToken;
	/** D35: recognized, but the engine has no mechanics — consumers refuse it by name. */
	readonly isUnsupported: boolean;
	readonly objectPolicy: ObjectPolicy;
	/** D134: which side of the room the verb reaches for on a two-sided noun; `none` asks. */
	readonly objectPreference: ObjectPreference;
	/** D56: the particles this verb takes — see the schema's `particles` remarks. */
	readonly particles: ReadonlySet<string>;
	/** D261: the prepositions whose phrase says where the direct object is (`take … from`). */
	readonly objectLocatingPrepositions: ReadonlySet<string>;
	/** Preposition word → the role its phrase binds (declared, never guessed). */
	readonly prepositionRoles: ReadonlyMap<string, PrepositionRole>;
}

/**
 * The frozen, indexed vocabulary every pipeline stage consumes. Structurally
 * satisfies `ConjunctionSplitVocabulary`.
 */
export interface CompiledLexicon {
	readonly directionsByToken: ReadonlyMap<string, DirectionToken>;
	/** Every single-word intent token and alias → its intent. */
	readonly intentsByToken: ReadonlyMap<string, CompiledIntent>;
	isClauseLeadingToken(tokenText: string): boolean;
	/**
	 * What a bare direction parses as: the authored `movementIntentToken`'s
	 * record, or a synthesized `BUILTIN_MOVEMENT_INTENT` record when unset.
	 */
	readonly movementIntent: CompiledIntent;
	/** First word → multiword aliases starting with it, longest sequence first (greedy matching). */
	readonly multiwordAliasesByFirstToken: ReadonlyMap<string, readonly MultiwordAlias[]>;
	/**
	 * Every preposition declared by ANY intent — the grammar's evidence base for
	 * `unbound_preposition` diagnostics (a word some intent binds, just not this
	 * one). Roles stay per-intent; this set carries no roles.
	 */
	readonly prepositionTokens: ReadonlySet<string>;
	/** D29: tokens that open a question — the shape layer's routing evidence. */
	readonly interrogativeLeaderSet: ReadonlySet<string>;
	/** D252: tokens that open an order to somebody (`tell <body> to <clause>`). */
	readonly orderLeaderSet: ReadonlySet<string>;
	/**
	 * D28: words recognized as LANGUAGE without mapping to anything —
	 * register evidence only (real English vs gibberish); never resolution.
	 */
	readonly recognitionWordSet: ReadonlySet<string>;
	/** D29: tokens whose short clause is social noise. */
	readonly socialWordSet: ReadonlySet<string>;
	/** D29: tokens that open a declaration. */
	readonly statementLeaderSet: ReadonlySet<string>;
	readonly stopwordSet: ReadonlySet<string>;
	/** Sorted, deduplicated single words — the fuzzy-correction candidate pool (plan §6.2). */
	readonly vocabularyTokens: readonly string[];
}

/** The outcome of one compilation (result-typed; issues are author-facing). */
export type LexiconCompilationOutcome =
	| { readonly issues: readonly string[]; readonly type: 'invalid' }
	| { readonly lexicon: CompiledLexicon; readonly type: 'compiled' };

/** One multiword alias (`pick up`, `look at`) and what it canonicalizes to. */
export interface MultiwordAlias {
	readonly target: MultiwordAliasTarget;
	readonly tokenSequence: readonly string[];
}

/** What a multiword alias resolves to. */
export type MultiwordAliasTarget =
	| { readonly kind: 'direction'; readonly token: DirectionToken }
	| { readonly kind: 'intent'; readonly token: IntentToken };

/** Mutable accumulation state threaded through the collection helpers. */
interface CompilationState {
	readonly claimedTokens: Map<string, string>;
	readonly directionsByToken: Map<string, DirectionToken>;
	readonly intentsByToken: Map<string, CompiledIntent>;
	readonly issues: string[];
	readonly multiwordAliasesByFirstToken: Map<string, MultiwordAlias[]>;
	readonly prepositionTokens: Set<string>;
	readonly stopwordSet: Set<string>;
	readonly vocabularySet: Set<string>;
}

/**
 * Compile an authored vocabulary into the indexed form the parser consumes.
 *
 * @param definition - The authored vocabulary
 * @returns The compiled lexicon, or every validation issue found
 */
export const compileLexicon = (definition: LexiconDefinition): LexiconCompilationOutcome => {
	const state: CompilationState = {
		claimedTokens: new Map(),
		directionsByToken: new Map(),
		intentsByToken: new Map(),
		issues: [],
		multiwordAliasesByFirstToken: new Map(),
		prepositionTokens: new Set(),
		stopwordSet: new Set(BUILTIN_STOPWORDS),
		vocabularySet: new Set(),
	};

	if (definition.intents.length === 0) {
		state.issues.push('intents: at least one intent is required — a vocabulary with no verbs cannot parse commands');
	}

	collectStopwords(definition, state);
	collectDirections(definition, state);
	collectIntents(definition, state);

	const movementIntent = resolveMovementIntent(definition, state);

	if (state.issues.length > 0) {
		return { issues: state.issues, type: 'invalid' };
	}

	// Longest-first within each first-word bucket, so greedy matching is correct;
	// ties broken lexicographically for determinism.
	for (const aliasBucket of state.multiwordAliasesByFirstToken.values()) {
		aliasBucket.sort(
			(leftAlias, rightAlias) =>
				rightAlias.tokenSequence.length - leftAlias.tokenSequence.length ||
				leftAlias.tokenSequence.join(' ').localeCompare(rightAlias.tokenSequence.join(' '), 'en'),
		);
	}

	const lexicon: CompiledLexicon = Object.freeze({
		directionsByToken: state.directionsByToken,
		intentsByToken: state.intentsByToken,
		interrogativeLeaderSet: new Set((definition.interrogativeLeaders ?? []).map(normalizeAuthoredToken)),
		isClauseLeadingToken: (tokenText: string): boolean =>
			state.intentsByToken.has(tokenText) ||
			state.directionsByToken.has(tokenText) ||
			state.multiwordAliasesByFirstToken.has(tokenText),
		movementIntent,
		multiwordAliasesByFirstToken: state.multiwordAliasesByFirstToken,
		orderLeaderSet: new Set((definition.orderLeaders ?? []).map(normalizeAuthoredToken)),
		prepositionTokens: state.prepositionTokens,
		recognitionWordSet: new Set((definition.recognitionWords ?? []).map(normalizeAuthoredToken)),
		socialWordSet: new Set((definition.socialWords ?? []).map(normalizeAuthoredToken)),
		statementLeaderSet: new Set((definition.statementLeaders ?? []).map(normalizeAuthoredToken)),
		stopwordSet: state.stopwordSet,
		vocabularyTokens: [...state.vocabularySet].sort((leftWord, rightWord) => leftWord.localeCompare(rightWord, 'en')),
	});

	return { lexicon, type: 'compiled' };
};

/**
 * Validate + register the caller's extra stopwords.
 *
 * @param definition - The authored vocabulary
 * @param state - Accumulation state (mutated)
 */
const collectStopwords = (definition: LexiconDefinition, state: CompilationState): void => {
	for (const [stopwordIndex, rawStopword] of (definition.stopwords ?? []).entries()) {
		const stopword = normalizeAuthoredToken(rawStopword);
		const location = `stopwords[${String(stopwordIndex)}]`;

		if (stopword === '') {
			state.issues.push(`${location}: blank stopword`);
			continue;
		}

		if (stopword.includes(' ')) {
			state.issues.push(`${location} ("${stopword}"): stopwords must be single words`);
			continue;
		}

		if (stopword.includes(':')) {
			state.issues.push(`${location} ("${stopword}"): ${COLON_TOKEN_ISSUE}`);
			continue;
		}

		state.stopwordSet.add(stopword);
	}
};

/**
 * Validate + register every direction and its aliases.
 *
 * @param definition - The authored vocabulary
 * @param state - Accumulation state (mutated)
 */
const collectDirections = (definition: LexiconDefinition, state: CompilationState): void => {
	for (const [directionIndex, direction] of definition.directions.entries()) {
		const location = `directions[${String(directionIndex)}]`;
		const canonicalText = normalizeAuthoredToken(direction.canonicalToken);

		if (!isValidSingleWord(canonicalText)) {
			state.issues.push(`${location}: canonicalToken must be a non-blank single word; received "${canonicalText}"`);
			continue;
		}

		const directionToken = toDirectionToken(canonicalText);

		registerSingleWordClaim(canonicalText, `${location} ("${canonicalText}")`, state, () => {
			state.directionsByToken.set(canonicalText, directionToken);
		});

		for (const rawAlias of direction.aliases) {
			const aliasText = normalizeAuthoredToken(rawAlias);
			const aliasLocation = `${location} ("${canonicalText}") alias "${aliasText}"`;

			if (aliasText === '') {
				state.issues.push(`${aliasLocation}: blank alias`);
				continue;
			}

			if (aliasText === canonicalText) {
				continue; // Harmless self-alias — dedupe silently.
			}

			// D220: a direction may be spelled in two words (`north east`, `south
			// west`), exactly as a verb may (`pick up`). The canonicalizer has
			// always resolved a multiword alias to a direction; only this rule kept
			// an author from declaring one.
			if (aliasText.includes(' ')) {
				registerMultiwordAlias(aliasText, { kind: 'direction', token: directionToken }, aliasLocation, state);
				continue;
			}

			registerSingleWordClaim(aliasText, aliasLocation, state, () => {
				state.directionsByToken.set(aliasText, directionToken);
			});
		}
	}
};

/**
 * Validate + register every intent, its aliases (single and multiword), and
 * its preposition bindings.
 *
 * @param definition - The authored vocabulary
 * @param state - Accumulation state (mutated)
 */
const collectIntents = (definition: LexiconDefinition, state: CompilationState): void => {
	for (const [intentIndex, intent] of definition.intents.entries()) {
		const location = `intents[${String(intentIndex)}]`;
		const canonicalText = normalizeAuthoredToken(intent.canonicalToken);

		if (!isValidSingleWord(canonicalText)) {
			state.issues.push(`${location}: canonicalToken must be a non-blank single word; received "${canonicalText}"`);
			continue;
		}

		// D134: a preference on a verb that takes no object is dead content —
		// the D31/D47 law applied to vocabulary.
		if (intent.objectPreference !== undefined && intent.objectPolicy === 'forbidden') {
			state.issues.push(
				`${location} ("${canonicalText}"): objectPreference "${intent.objectPreference}" on an intent whose objectPolicy forbids objects can never apply`,
			);
		}

		const bareDirection = compileBareDirection(intent, `${location} ("${canonicalText}")`, state.directionsByToken, (issue) => state.issues.push(issue));
		const compiledIntent: CompiledIntent = {
			...(bareDirection === undefined ? {} : { bareDirection }),
			canonicalToken: toIntentToken(canonicalText),
			isUnsupported: intent.isUnsupported === true,
			objectPolicy: intent.objectPolicy,
			objectPreference: intent.objectPreference ?? 'none',
			particles: compileParticles(intent, `${location} ("${canonicalText}")`, state),
			...collectPrepositionBindings(intent, `${location} ("${canonicalText}")`, state),
		};

		registerSingleWordClaim(canonicalText, `${location} ("${canonicalText}")`, state, () => {
			state.intentsByToken.set(canonicalText, compiledIntent);
		});

		for (const rawAlias of intent.aliases) {
			const aliasText = normalizeAuthoredToken(rawAlias);
			const aliasLocation = `${location} ("${canonicalText}") alias "${aliasText}"`;

			if (aliasText === '') {
				state.issues.push(`${aliasLocation}: blank alias`);
				continue;
			}

			if (aliasText === canonicalText) {
				continue; // Harmless self-alias — dedupe silently.
			}

			if (aliasText.includes(' ')) {
				registerMultiwordAlias(
					aliasText,
					{ kind: 'intent', token: compiledIntent.canonicalToken },
					aliasLocation,
					state,
				);
				continue;
			}

			registerSingleWordClaim(aliasText, aliasLocation, state, () => {
				state.intentsByToken.set(aliasText, compiledIntent);
			});
		}
	}
};

/**
 * Resolve what bare directions parse as: the authored `movementIntentToken`'s
 * compiled record, or a synthesized builtin fallback (D14 hybrid).
 *
 * @param definition - The authored vocabulary
 * @param state - Accumulation state (mutated: issues)
 * @returns The movement intent record (the fallback also stands in when the authored token is invalid — unused, since compilation then fails)
 */
const resolveMovementIntent = (definition: LexiconDefinition, state: CompilationState): CompiledIntent => {
	const builtinFallback: CompiledIntent = {
		canonicalToken: toIntentToken(BUILTIN_MOVEMENT_INTENT),
		isUnsupported: false,
		objectLocatingPrepositions: new Set(),
		objectPolicy: 'forbidden',
		objectPreference: 'none',
		particles: new Set(),
		prepositionRoles: new Map(),
	};

	if (definition.movementIntentToken === undefined) {
		return builtinFallback;
	}

	const movementToken = normalizeAuthoredToken(definition.movementIntentToken);
	const declaredIntent = state.intentsByToken.get(movementToken);

	if (declaredIntent === undefined || declaredIntent.canonicalToken !== movementToken) {
		state.issues.push(
			`movementIntentToken ("${movementToken}"): must name a declared intent's canonicalToken (aliases do not qualify)`,
		);

		return builtinFallback;
	}

	return declaredIntent;
};

/**
 * Split with the ≥1-element guarantee stated in the TYPE: `String.split`
 * never returns an empty array (`''.split(' ')` is `['']`), so indexing the
 * first element needs no runtime fallback — a dead `?? ''` here previously
 * escaped the coverage gate entirely (Calder's 2026-08-13 finding; local
 * helper per the R1 ruling until a second indexing split appears).
 *
 * @param text - The text to split
 * @param separator - The separator to split on
 * @returns The split sequence, typed non-empty
 */
const splitIntoNonEmptySequence = (text: string, separator: string): readonly [string, ...string[]] =>
	text.split(separator) as [string, ...string[]];

/**
 * Validate one intent's particles and register them as legal vocabulary (D59).
 *
 * @remarks
 * The D57 review found particles bypassing every registration path: absent
 * from `vocabularyTokens`, a 4+ letter particle was fuzzy-correction BAIT — an
 * unknown word eligible to be rewritten into a nearby entity noun. Registering
 * them here closes that; the single-word check exists because a multiword
 * particle could never match one token and would be authored dead content.
 *
 * @param intent - The authored intent
 * @param location - Author-facing locator for issues
 * @param state - Accumulation state (mutated: issues, vocabularySet)
 * @returns The compiled particle set
 */
const compileParticles = (
	intent: IntentDefinition,
	location: string,
	state: CompilationState,
): ReadonlySet<string> => {
	const particles = new Set<string>();

	for (const rawParticle of intent.particles ?? []) {
		const particleText = normalizeAuthoredToken(rawParticle);

		if (!isValidSingleWord(particleText)) {
			state.issues.push(`${location}: particles must be non-blank single words; received "${particleText}"`);
			continue;
		}

		particles.add(particleText);
		state.vocabularySet.add(particleText);
	}

	return particles;
};

/**
 * Register a multiword alias into its first-word bucket, guarding duplicates
 * and reserved-word components.
 *
 * @param aliasText - The normalized multiword alias (`pick up`)
 * @param target - What the alias canonicalizes to
 * @param aliasLocation - Author-facing locator for issue messages
 * @param state - Accumulation state (mutated)
 */
const registerMultiwordAlias = (
	aliasText: string,
	target: MultiwordAliasTarget,
	aliasLocation: string,
	state: CompilationState,
): void => {
	const tokenSequence = splitIntoNonEmptySequence(aliasText, ' ');

	// EVERY component must survive to matching time: reserved words split or
	// resolve elsewhere, stopwords are stripped before multiword matching, and
	// a `:` can never emerge from character folding — any of them makes the
	// alias permanently unmatchable, which is an authoring error, not a runtime
	// mystery.
	for (const componentWord of tokenSequence) {
		if (RESERVED_FUNCTION_WORDS.has(componentWord) || state.stopwordSet.has(componentWord)) {
			state.issues.push(
				`${aliasLocation}: component "${componentWord}" is a reserved function word or stopword — it would be split or stripped before matching, so this alias could never match`,
			);

			return;
		}

		if (componentWord.includes(':')) {
			state.issues.push(`${aliasLocation}: ${COLON_TOKEN_ISSUE}`);

			return;
		}
	}

	const firstWord = tokenSequence[0];

	const existingClaim = state.claimedTokens.get(aliasText);

	if (existingClaim !== undefined) {
		state.issues.push(`${aliasLocation}: already claimed by ${existingClaim}`);

		return;
	}

	state.claimedTokens.set(aliasText, aliasLocation);

	const aliasBucket = state.multiwordAliasesByFirstToken.get(firstWord) ?? [];

	aliasBucket.push({ target, tokenSequence });
	state.multiwordAliasesByFirstToken.set(firstWord, aliasBucket);

	for (const componentWord of tokenSequence) {
		state.vocabularySet.add(componentWord);
	}
};

/**
 * Claim one single-word token for an owner, reporting reserved-word and
 * duplicate collisions; on success, run the registration and add the word to
 * the fuzzy vocabulary.
 *
 * @param tokenText - The normalized single-word token
 * @param ownerLocation - Author-facing locator for issue messages
 * @param state - Accumulation state (mutated)
 * @param registerOwner - Applies the successful registration
 */
const registerSingleWordClaim = (
	tokenText: string,
	ownerLocation: string,
	state: CompilationState,
	registerOwner: () => void,
): void => {
	if (RESERVED_FUNCTION_WORDS.has(tokenText) || state.stopwordSet.has(tokenText)) {
		state.issues.push(`${ownerLocation}: collides with a reserved function word or stopword`);

		return;
	}

	if (tokenText.includes(':')) {
		state.issues.push(`${ownerLocation}: ${COLON_TOKEN_ISSUE}`);

		return;
	}

	const existingClaim = state.claimedTokens.get(tokenText);

	if (existingClaim !== undefined) {
		state.issues.push(`${ownerLocation}: already claimed by ${existingClaim}`);

		return;
	}

	state.claimedTokens.set(tokenText, ownerLocation);
	registerOwner();
	state.vocabularySet.add(tokenText);
};
